import React, { useState, useEffect, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  AlertTriangle,
  Heart,
  Award,
  Users,
  ArrowUp,
  Home,
  Brain,
  ArrowDown,
  ThumbsUp,
  Filter,
  X,
  RotateCcw,
  Shield,
  Building2,
} from "lucide-react";

// Import the centralized calculation function
import { calculateFactorAnalysis } from "./rcCalculations";

const DomainImpactCard = ({ impact }) => {
  if (!impact) return null;

  const getIcon = () => {
    switch (impact.icon) {
      case "AlertTriangle":
        return <AlertTriangle className="text-red-400" size={16} />;
      case "Heart":
        return <Heart className="text-purple-400" size={16} />;
      case "Award":
        return <Award className="text-blue-400" size={16} />;
      case "Home":
        return <Home className="text-emerald-400" size={16} />;
      case "Brain":
        return <Brain className="text-red-400" size={16} />;
      case "Shield":
        return <Shield className="text-amber-400" size={16} />;
      case "Building2":
        return <Building2 className="text-orange-400" size={16} />;
      default:
        return null;
    }
  };

  const maxImpact = Math.max(...impact.domains.map((d) => Math.abs(d.impact)));

  return (
    <div className="bg-slate-900/50 p-3 rounded-lg">
      <h4 className="text-white font-medium flex items-center gap-2">
        {getIcon()}
        {impact.name}
      </h4>
      <div className="space-y-2 mt-3">
        {impact.domains.map((domain, i) => (
          <div key={i}>
            <div className="flex justify-between text-xs">
              <span className="text-slate-300">
                {domain.domain.charAt(0).toUpperCase() + domain.domain.slice(1)}
              </span>
              <span
                className={
                  domain.impact > 0 ? "text-emerald-400" : "text-red-400"
                }
              >
                {domain.impact > 0 ? "+" : ""}
                {domain.impact.toFixed(1)}
              </span>
            </div>
            <div className="w-full bg-slate-700/30 h-1.5 rounded-full">
              <div
                className={`h-1.5 rounded-full ${
                  domain.impact > 0 ? impact.color : "bg-red-500"
                }`}
                style={{
                  width: `${Math.min(
                    (Math.abs(domain.impact) / maxImpact) * 100,
                    100
                  )}%`,
                }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const FilterToggle = ({
  label,
  isActive,
  onClick,
  type = "risk",
  count = 0,
}) => {
  const getTypeStyles = () => {
    switch (type) {
      case "protective":
        return isActive
          ? "bg-emerald-500/20 border-emerald-400 text-emerald-300"
          : "bg-slate-700/50 border-slate-600 text-slate-300 hover:border-emerald-500";
      case "risk":
        return isActive
          ? "bg-red-500/20 border-red-400 text-red-300"
          : "bg-slate-700/50 border-slate-600 text-slate-300 hover:border-red-500";
      default:
        return isActive
          ? "bg-blue-500/20 border-blue-400 text-blue-300"
          : "bg-slate-700/50 border-slate-600 text-slate-300 hover:border-blue-500";
    }
  };

  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${getTypeStyles()}`}
    >
      <div className="flex items-center gap-2">
        <span>{label}</span>
        {count > 0 && (
          <span className="text-xs bg-slate-600 px-1.5 py-0.5 rounded">
            {count}
          </span>
        )}
      </div>
    </button>
  );
};

const RCKeyFactorsTab = ({ organizationId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allData, setAllData] = useState(null);
  const [analysisSettings, setAnalysisSettings] = useState({
    minSampleSize: 5,
    maxRelapseRate: 70,
    minRCScore: 0,
    sortBy: "relapseRate",
  });

  // Filter states
  const [selectedFactors, setSelectedFactors] = useState({
    // Risk factors
    trauma: false,
    cod: false,
    prison: false,
    homeless: false,
    guilt: false,

    // Protective factors
    purpose: false,
    twoParentHome: false,
    therapy: false,
    strongKidsRelation: false,
  });

  // Available filters with metadata
  const availableFilters = {
    riskFactors: [
      { key: "trauma", label: "Childhood Trauma", icon: AlertTriangle },
      { key: "cod", label: "Co-occurring Diagnosis", icon: Brain },
      { key: "prison", label: "Prison Experience (1+ years)", icon: Shield },
      { key: "homeless", label: "Homelessness (1+ years)", icon: Building2 },
      { key: "guilt", label: "Has Guilt/Shame", icon: Heart },
    ],
    protectiveFactors: [
      { key: "purpose", label: "Sense of Purpose", icon: Award },
      { key: "twoParentHome", label: "Two-Parent Home", icon: Home },
      { key: "therapy", label: "In Therapy", icon: Users },
      {
        key: "strongKidsRelation",
        label: "Strong Kids Relationship",
        icon: Heart,
      },
    ],
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!organizationId) throw new Error("Organization ID is required");
        setLoading(true);

        // Use the specific function for factor analysis
        const data = await calculateFactorAnalysis(organizationId);
        setAllData(data);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [organizationId]);

  // Helper functions
  const checkResponse = (v) => {
    const normalizedValue = String(v || "")
      .toLowerCase()
      .trim();
    if (normalizedValue.startsWith("y")) return "yes";
    if (normalizedValue.startsWith("n") && !normalizedValue.includes("n/a"))
      return "no";
    return "na";
  };

  const hasGuiltShame = (v) => {
    const n = Number(v);
    return n === 3 || n === 4;
  };

  const isInTherapy = (v) => {
    const n = Number(v);
    return n === 3 || n === 4;
  };

  const hasStrongKidsRelation = (v) => {
    const n = Number(v);
    return n === 3 || n === 4;
  };

  const isRelapse = (s) =>
    ["not sober"].includes(
      String(s || "")
        .toLowerCase()
        .trim()
    );

  const calculateOverallRelapseRate = (rawBuckets) => {
    let totalCount = 0;
    let totalRelapses = 0;

    Object.values(rawBuckets).forEach((bucket) => {
      Object.values(bucket).forEach((group) => {
        if (group.c && group.r !== undefined) {
          totalCount += group.c;
          totalRelapses += group.r;
        }
      });
    });

    return totalCount > 0 ? (totalRelapses / totalCount) * 100 : 0;
  };

  const filterCombinations = (combinations, activeFactors) => {
    if (activeFactors.length < 2) return {};

    return Object.fromEntries(
      Object.entries(combinations).filter(([key, combination]) => {
        const factorA = combination.factorA.toLowerCase();
        const factorB = combination.factorB.toLowerCase();
        return activeFactors.some(
          (factor) =>
            factorA.includes(factor.toLowerCase()) ||
            factorB.includes(factor.toLowerCase())
        );
      })
    );
  };

  const filterDomainImpacts = (domainImpacts, activeFactors) => {
    // Map filter keys to domain impact keys
    const keyMapping = {
      trauma: "trauma",
      cod: "cod", // ← Fix the mismatch
      prison: "prison",
      homeless: "homeless",
      purpose: "purpose",
      twoParentHome: "twoParentHome",
      guilt: "guilt", // ← Another potential mismatch
      therapy: "therapy",
      strongKidsRelation: "strongKidsRelation",
    };

    // Convert activeFactors to the correct domain impact keys
    const mappedFactors = activeFactors.map(
      (factor) => keyMapping[factor] || factor
    );

    return Object.fromEntries(
      Object.entries(domainImpacts).filter(([key, _]) =>
        mappedFactors.includes(key)
      )
    );
  };

  const calculateTraumaImpact = (traumaRelapse) => {
    const withTraumaRCAvg = traumaRelapse.withTrauma.c
      ? traumaRelapse.withTrauma.rc / traumaRelapse.withTrauma.c
      : 0;

    const withoutTraumaRCAvg = traumaRelapse.withoutTrauma.c
      ? traumaRelapse.withoutTrauma.rc / traumaRelapse.withoutTrauma.c
      : 0;

    const withTraumaRelapseRate = traumaRelapse.withTrauma.c
      ? (traumaRelapse.withTrauma.r / traumaRelapse.withTrauma.c) * 100
      : 0;

    const withoutTraumaRelapseRate = traumaRelapse.withoutTrauma.c
      ? (traumaRelapse.withoutTrauma.r / traumaRelapse.withoutTrauma.c) * 100
      : 0;

    return [
      {
        name: "With Trauma",
        rcScore: parseFloat(withTraumaRCAvg.toFixed(1)),
        relapse: parseFloat(withTraumaRelapseRate.toFixed(1)),
        count: Math.round(traumaRelapse.withTrauma.c),
      },
      {
        name: "Without Trauma",
        rcScore: parseFloat(withoutTraumaRCAvg.toFixed(1)),
        relapse: parseFloat(withoutTraumaRelapseRate.toFixed(1)),
        count: Math.round(traumaRelapse.withoutTrauma.c),
      },
    ];
  };

  // Calculate filtered data based on selected factors
  const filteredData = useMemo(() => {
    if (!allData) return null;

    const activeFactors = Object.entries(selectedFactors)
      .filter(([_, isActive]) => isActive)
      .map(([key, _]) => key);

    if (activeFactors.length === 0) {
      return {
        factorRankings: allData.factorRankings,
        factorCombinations: allData.factorCombinations,
        domainImpacts: allData.domainImpacts,
        riskMetrics: allData.riskMetrics,
        filteredResidents: allData.rowsForStats,
        filterSummary: {
          totalResidents: allData.rcData.totalUniqueIndividuals,
          averageRC: allData.rcData.rcMean,
          relapseRate: calculateOverallRelapseRate(allData.rawBuckets),
        },
      };
    }

    // Filter residents based on selected criteria
    const filteredResidents = allData.rowsForStats.filter((resident) => {
      return activeFactors.every((factor) => {
        switch (factor) {
          case "trauma":
            return checkResponse(resident.childhoodTrauma) === "yes";
          case "cod":
            return checkResponse(resident.coOccurringDiagnosis) === "yes";
          case "prison":
            return checkResponse(resident.overOneYearInPrison) === "yes";
          case "homeless":
            return checkResponse(resident.homelessOneYear) === "yes";
          case "purpose":
            const purposeItems = ["lifeHasPurpose", "greaterPurpose"]
              .map((f) => +resident[f])
              .filter((v) => !Number.isNaN(v));
            return (
              purposeItems.length &&
              purposeItems.reduce((s, v) => s + v, 0) / purposeItems.length >= 3
            );
          case "twoParentHome":
            return checkResponse(resident.twoParentsHome) === "yes";
          case "guilt":
            return hasGuiltShame(resident.guiltOrShame);
          case "therapy":
            return isInTherapy(resident.therapy);
          case "strongKidsRelation":
            return (
              checkResponse(resident.kids) === "yes" &&
              hasStrongKidsRelation(resident.strongRelationKids)
            );
          default:
            return true;
        }
      });
    });

    // Calculate statistics for filtered group
    const rcScores = filteredResidents.map((r) => +r.recoveryCapitalTotal || 0);
    const averageRC =
      rcScores.length > 0
        ? rcScores.reduce((sum, score) => sum + score, 0) / rcScores.length
        : 0;

    // Calculate relapse rate ONLY for residents with known outcomes (alumni)
    const filteredHistoricalResidents = filteredResidents.filter((r) => {
      const residentName =
        r.residentName || r.fullName || `${r.firstName} ${r.lastName}`.trim();
      const alumni = allData.alumniMap.get(residentName);
      return alumni && alumni.sobrietyStatus; // Only include those with known outcomes
    });

    const relapses = filteredHistoricalResidents.filter((r) => {
      const residentName =
        r.residentName || r.fullName || `${r.firstName} ${r.lastName}`.trim();
      return isRelapse(allData.alumniMap.get(residentName)?.sobrietyStatus);
    }).length;

    const relapseRate =
      filteredHistoricalResidents.length > 0
        ? (relapses / filteredHistoricalResidents.length) * 100
        : 0;

    return {
      factorRankings: allData.factorRankings.filter((ranking) =>
        activeFactors.some((factor) =>
          ranking.factor.toLowerCase().includes(factor.toLowerCase())
        )
      ),
      factorCombinations: filterCombinations(
        allData.factorCombinations,
        activeFactors
      ),
      domainImpacts: filterDomainImpacts(allData.domainImpacts, activeFactors),
      riskMetrics: allData.riskMetrics,
      filteredResidents,
      filterSummary: {
        totalResidents: filteredResidents.length,
        averageRC: +averageRC.toFixed(1),
        relapseRate: +relapseRate.toFixed(1),
      },
    };
  }, [allData, selectedFactors]);

  const toggleFactor = (factorKey) => {
    setSelectedFactors((prev) => ({
      ...prev,
      [factorKey]: !prev[factorKey],
    }));
  };

  const clearFilters = () => {
    setSelectedFactors(
      Object.fromEntries(
        Object.keys(selectedFactors).map((key) => [key, false])
      )
    );
  };

  const hasActiveFilters = Object.values(selectedFactors).some(Boolean);

  if (allData && allData.rowsForStats) {
    // Define all possible factors and their check functions
    const allFactors = {
      // Risk factors
      trauma: (r) => checkResponse(r.childhoodTrauma) === "yes",
      cod: (r) => checkResponse(r.coOccurringDiagnosis) === "yes",
      prison: (r) => checkResponse(r.overOneYearInPrison) === "yes",
      homeless: (r) => checkResponse(r.homelessOneYear) === "yes",
      guilt: (r) => hasGuiltShame(r.guiltOrShame),

      // Protective factors
      purpose: (r) => {
        const purposeItems = ["lifeHasPurpose", "greaterPurpose"]
          .map((f) => +r[f])
          .filter((v) => !Number.isNaN(v));
        return (
          purposeItems.length &&
          purposeItems.reduce((s, v) => s + v, 0) / purposeItems.length >= 3
        );
      },
      twoParentHome: (r) => checkResponse(r.twoParentsHome) === "yes",
      therapy: (r) => isInTherapy(r.therapy),
      strongKidsRelation: (r) =>
        checkResponse(r.kids) === "yes" &&
        hasStrongKidsRelation(r.strongRelationKids),

      // Additional factors we can check
      kids: (r) => checkResponse(r.kids) === "yes",
      married: (r) => checkResponse(r.married) === "yes",
      // Add age groups
      young: (r) => {
        const age = +r.age;
        return age >= 18 && age <= 25;
      },
      middleAge: (r) => {
        const age = +r.age;
        return age >= 26 && age <= 35;
      },
      older: (r) => {
        const age = +r.age;
        return age >= 36;
      },
    };

    const factorLabels = {
      trauma: "Childhood Trauma",
      cod: "Co-occurring Diagnosis",
      prison: "Prison Experience",
      homeless: "Homelessness",
      guilt: "Guilt/Shame",
      purpose: "Sense of Purpose",
      twoParentHome: "Two-Parent Home",
      therapy: "In Therapy",
      strongKidsRelation: "Strong Kids Relationship",
      kids: "Has Children",
      married: "Married",
      young: "Age 18-25",
      middleAge: "Age 26-35",
      older: "Age 36+",
    };

    const calculateStats = (residents) => {
      if (residents.length === 0)
        return { count: 0, avgRC: 0, relapseRate: 0, alumniCount: 0 };

      const rcScores = residents.map((r) => +r.recoveryCapitalTotal || 0);
      const avgRC =
        rcScores.reduce((sum, score) => sum + score, 0) / rcScores.length;

      // Only count alumni with known outcomes for relapse rate
      const alumniWithOutcomes = residents.filter((r) => {
        const name =
          r.residentName || r.fullName || `${r.firstName} ${r.lastName}`.trim();
        const alumni = allData.alumniMap.get(name);
        return alumni && alumni.sobrietyStatus;
      });

      const relapses = alumniWithOutcomes.filter((r) => {
        const name =
          r.residentName || r.fullName || `${r.firstName} ${r.lastName}`.trim();
        return isRelapse(allData.alumniMap.get(name)?.sobrietyStatus);
      }).length;

      const relapseRate =
        alumniWithOutcomes.length > 0
          ? (relapses / alumniWithOutcomes.length) * 100
          : 0;

      return {
        count: residents.length,
        avgRC: +avgRC.toFixed(1),
        relapseRate: +relapseRate.toFixed(1),
        alumniCount: alumniWithOutcomes.length,
      };
    };

    // 1. FIND ALL POSSIBLE COMBINATIONS (up to 4 factors for performance)
    const factorKeys = Object.keys(allFactors);
    const allCombinations = [];

    // Generate all combinations of 1-4 factors
    for (let size = 1; size <= 4; size++) {
      const combinations = getCombinations(factorKeys, size);

      combinations.forEach((combo) => {
        // Filter residents who match ALL factors in this combination
        const matchingResidents = allData.rowsForStats.filter((resident) => {
          return combo.every((factor) => allFactors[factor](resident));
        });

        const stats = calculateStats(matchingResidents);

        // Only include combinations with at least 3 alumni with outcomes
        if (stats.alumniCount >= analysisSettings.minSampleSize) {
          allCombinations.push({
            factors: combo.map((f) => factorLabels[f]),
            factorKeys: combo,
            stats: stats,
            profile: combo.join(" + "),
          });
        }
      });
    }

    // Helper function to generate combinations
    function getCombinations(arr, size) {
      if (size === 1) return arr.map((item) => [item]);

      const combinations = [];
      for (let i = 0; i < arr.length; i++) {
        const smaller = getCombinations(arr.slice(i + 1), size - 1);
        smaller.forEach((combo) => {
          combinations.push([arr[i], ...combo]);
        });
      }
      return combinations;
    }

    // 2. RANK BY LOWEST RELAPSE RATE

    const bestOutcomes = allCombinations
      .sort((a, b) => {
        // First by relapse rate (lower is better)
        if (a.stats.relapseRate !== b.stats.relapseRate) {
          return a.stats.relapseRate - b.stats.relapseRate;
        }
        // Then by sample size (larger is better)
        return b.stats.alumniCount - a.stats.alumniCount;
      })
      .slice(0, 20);

    bestOutcomes.forEach((combo, index) => {});

    // 3. ZERO RELAPSE COMBINATIONS

    const zeroRelapseProfiles = allCombinations.filter(
      (combo) => combo.stats.relapseRate === 0
    );

    if (zeroRelapseProfiles.length > 0) {
      // Sort by sample size (largest first)
      zeroRelapseProfiles
        .sort((a, b) => b.stats.alumniCount - a.stats.alumniCount)
        .forEach((combo, index) => {
          
        });
    } else {
      console.log("No combinations found with 0% relapse rate");
    }

    // 4. BEST COMBINATIONS BY SAMPLE SIZE

    const largeSampleBest = allCombinations
      .filter((combo) => combo.stats.alumniCount >= 10)
      .sort((a, b) => a.stats.relapseRate - b.stats.relapseRate)
      .slice(0, 10);

    largeSampleBest.forEach((combo, index) => {});

    // 5. PROTECTIVE VS RISK FACTOR ANALYSIS

    const riskFactors = ["trauma", "cod", "prison", "homeless", "guilt"];
    const protectiveFactors = [
      "purpose",
      "twoParentHome",
      "therapy",
      "strongKidsRelation",
    ];

    const analyzeFactorBalance = (factorKeys) => {
      const riskCount = factorKeys.filter((f) =>
        riskFactors.includes(f)
      ).length;
      const protectiveCount = factorKeys.filter((f) =>
        protectiveFactors.includes(f)
      ).length;
      return {
        riskCount,
        protectiveCount,
        ratio: protectiveCount / Math.max(riskCount, 1),
      };
    };

    // Group by factor balance
    const factorBalanceGroups = {
      "Pure Protective (0 risk)": [],
      "Protective Heavy (2:1+ ratio)": [],
      "Balanced (1:1 ratio)": [],
      "Risk Heavy (1:2+ ratio)": [],
      "Pure Risk (0 protective)": [],
    };

    allCombinations.forEach((combo) => {
      const balance = analyzeFactorBalance(combo.factorKeys);

      if (balance.riskCount === 0) {
        factorBalanceGroups["Pure Protective (0 risk)"].push(combo);
      } else if (balance.ratio >= 2) {
        factorBalanceGroups["Protective Heavy (2:1+ ratio)"].push(combo);
      } else if (balance.ratio >= 0.8 && balance.ratio <= 1.2) {
        factorBalanceGroups["Balanced (1:1 ratio)"].push(combo);
      } else if (balance.ratio <= 0.5) {
        factorBalanceGroups["Risk Heavy (1:2+ ratio)"].push(combo);
      } else if (balance.protectiveCount === 0) {
        factorBalanceGroups["Pure Risk (0 protective)"].push(combo);
      }
    });

    Object.entries(factorBalanceGroups).forEach(([groupName, combos]) => {
      if (combos.length > 0) {
        const avgRelapse =
          combos.reduce((sum, c) => sum + c.stats.relapseRate, 0) /
          combos.length;
      

        // Show best in this category
        const best = combos.sort(
          (a, b) => a.stats.relapseRate - b.stats.relapseRate
        )[0];
       
      }
    });

    // 6. THE PERFECT RESIDENT PROFILE

    if (bestOutcomes.length > 0) {
      const perfectProfile = bestOutcomes[0];
    

      // Show the actual residents who match this profile
      const perfectResidents = allData.rowsForStats.filter((resident) => {
        return perfectProfile.factorKeys.every((factor) =>
          allFactors[factor](resident)
        );
      });

      perfectResidents.forEach((resident, index) => {
        const name =
          resident.residentName ||
          resident.fullName ||
          `${resident.firstName} ${resident.lastName}`.trim();
        const alumni = allData.alumniMap.get(name);
        const rcScore = +resident.recoveryCapitalTotal || 0;
        const status = alumni ? alumni.sobrietyStatus : "Current Resident";
      });
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  const hasFilteredData =
    filteredData && filteredData.filteredResidents.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <div className="h-10 w-1 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full" />
        <h2 className="text-2xl font-bold text-white">
          Key Factors Influencing Recovery
        </h2>
      </div>

      {/* Dynamic Filters Section */}
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-blue-400 flex items-center gap-2">
            <Filter size={20} />
            Factor Filters
          </h3>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm transition-colors"
            >
              <RotateCcw size={14} />
              Clear All
            </button>
          )}
        </div>

        {/* Risk Factors */}
        <div className="mb-4">
          <h4 className="text-red-400 font-medium mb-2 flex items-center gap-2">
            <AlertTriangle size={16} />
            Risk Factors
          </h4>
          <div className="flex flex-wrap gap-2">
            {availableFilters.riskFactors.map((filter) => (
              <FilterToggle
                key={filter.key}
                label={filter.label}
                isActive={selectedFactors[filter.key]}
                onClick={() => toggleFactor(filter.key)}
                type="risk"
                count={
                  allData
                    ? allData.rowsForStats.filter((r) => {
                        switch (filter.key) {
                          case "trauma":
                            return checkResponse(r.childhoodTrauma) === "yes";
                          case "cod":
                            return (
                              checkResponse(r.coOccurringDiagnosis) === "yes"
                            );
                          case "prison":
                            return (
                              checkResponse(r.overOneYearInPrison) === "yes"
                            );
                          case "homeless":
                            return checkResponse(r.homelessOneYear) === "yes";
                          default:
                            return false;
                        }
                      }).length
                    : 0
                }
              />
            ))}
          </div>
        </div>

        {/* Protective Factors */}
        <div className="mb-4">
          <h4 className="text-emerald-400 font-medium mb-2 flex items-center gap-2">
            <ThumbsUp size={16} />
            Protective Factors
          </h4>
          <div className="flex flex-wrap gap-2">
            {availableFilters.protectiveFactors.map((filter) => (
              <FilterToggle
                key={filter.key}
                label={filter.label}
                isActive={selectedFactors[filter.key]}
                onClick={() => toggleFactor(filter.key)}
                type="protective"
                count={
                  allData
                    ? allData.rowsForStats.filter((r) => {
                        switch (filter.key) {
                          case "purpose":
                            const purposeItems = [
                              "lifeHasPurpose",
                              "greaterPurpose",
                            ]
                              .map((f) => +r[f])
                              .filter((v) => !Number.isNaN(v));
                            return (
                              purposeItems.length &&
                              purposeItems.reduce((s, v) => s + v, 0) /
                                purposeItems.length >=
                                3
                            );
                          case "twoParentHome":
                            return checkResponse(r.twoParentsHome) === "yes";
                          case "guilt":
                            return hasGuiltShame(r.guiltOrShame);
                          case "therapy":
                            return isInTherapy(r.therapy);
                          case "strongKidsRelation":
                            return (
                              checkResponse(r.kids) === "yes" &&
                              hasStrongKidsRelation(r.strongRelationKids)
                            );
                          default:
                            return false;
                        }
                      }).length
                    : 0
                }
              />
            ))}
          </div>
        </div>

        {/* Filter Summary */}
        {hasActiveFilters && (
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h4 className="text-white font-medium mb-2">Filter Results</h4>
            {hasFilteredData ? (
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-blue-400 text-2xl font-bold">
                    {filteredData.filterSummary.totalResidents}
                  </p>
                  <p className="text-slate-300 text-sm">Residents Match</p>
                </div>
                <div>
                  <p className="text-emerald-400 text-2xl font-bold">
                    {filteredData.filterSummary.averageRC}
                  </p>
                  <p className="text-slate-300 text-sm">Avg RC Score</p>
                </div>
                <div>
                  <p className="text-red-400 text-2xl font-bold">
                    {filteredData.filterSummary.relapseRate}%
                  </p>
                  <p className="text-slate-300 text-sm">Relapse Rate</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <X className="mx-auto text-red-400 mb-2" size={24} />
                <p className="text-red-400 font-medium">
                  No residents match the selected criteria
                </p>
                <p className="text-slate-400 text-sm">
                  Try adjusting your filters or selecting different combinations
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Enhanced 2-Factor Interaction Analysis */}
      {hasActiveFilters &&
        (() => {
          const activeFactors = Object.entries(selectedFactors)
            .filter(([_, isActive]) => isActive)
            .map(([key, _]) => key);

          if (activeFactors.length === 2 && hasFilteredData) {
            // Calculate 2x2 breakdown for exactly 2 factors
            const [factorA, factorB] = activeFactors;
            const factorALabel =
              availableFilters.riskFactors.find((f) => f.key === factorA)
                ?.label ||
              availableFilters.protectiveFactors.find((f) => f.key === factorA)
                ?.label ||
              factorA;
            const factorBLabel =
              availableFilters.riskFactors.find((f) => f.key === factorB)
                ?.label ||
              availableFilters.protectiveFactors.find((f) => f.key === factorB)
                ?.label ||
              factorB;

            // Calculate the 4 groups
            const groups = {
              bothFactors: { residents: [], rcScores: [], relapses: 0 },
              factorAOnly: { residents: [], rcScores: [], relapses: 0 },
              factorBOnly: { residents: [], rcScores: [], relapses: 0 },
              neitherFactor: { residents: [], rcScores: [], relapses: 0 },
            };

            const alumniMap = allData.alumniMap;
            allData.rowsForStats.forEach((resident) => {
              const hasFactorA = (() => {
                switch (factorA) {
                  case "trauma":
                    return checkResponse(resident.childhoodTrauma) === "yes";
                  case "cod":
                    return (
                      checkResponse(resident.coOccurringDiagnosis) === "yes"
                    );
                  case "prison":
                    return (
                      checkResponse(resident.overOneYearInPrison) === "yes"
                    );
                  case "homeless":
                    return checkResponse(resident.homelessOneYear) === "yes";
                  case "purpose":
                    const purposeItems = ["lifeHasPurpose", "greaterPurpose"]
                      .map((f) => +resident[f])
                      .filter((v) => !Number.isNaN(v));
                    return (
                      purposeItems.length &&
                      purposeItems.reduce((s, v) => s + v, 0) /
                        purposeItems.length >=
                        3
                    );
                  case "twoParentHome":
                    return checkResponse(resident.twoParentsHome) === "yes";
                  case "guilt":
                    return hasGuiltShame(resident.guiltOrShame);
                  case "therapy":
                    return isInTherapy(resident.therapy);
                  case "strongKidsRelation":
                    return (
                      checkResponse(resident.kids) === "yes" &&
                      hasStrongKidsRelation(resident.strongRelationKids)
                    );
                  default:
                    return false;
                }
              })();

              const hasFactorB = (() => {
                switch (factorB) {
                  case "trauma":
                    return checkResponse(resident.childhoodTrauma) === "yes";
                  case "cod":
                    return (
                      checkResponse(resident.coOccurringDiagnosis) === "yes"
                    );
                  case "prison":
                    return (
                      checkResponse(resident.overOneYearInPrison) === "yes"
                    );
                  case "homeless":
                    return checkResponse(resident.homelessOneYear) === "yes";
                  case "purpose":
                    const purposeItems = ["lifeHasPurpose", "greaterPurpose"]
                      .map((f) => +resident[f])
                      .filter((v) => !Number.isNaN(v));
                    return (
                      purposeItems.length &&
                      purposeItems.reduce((s, v) => s + v, 0) /
                        purposeItems.length >=
                        3
                    );
                  case "twoParentHome":
                    return checkResponse(resident.twoParentsHome) === "yes";
                  case "guilt":
                    return hasGuiltShame(resident.guiltOrShame);
                  case "therapy":
                    return isInTherapy(resident.therapy);
                  case "strongKidsRelation":
                    return (
                      checkResponse(resident.kids) === "yes" &&
                      hasStrongKidsRelation(resident.strongRelationKids)
                    );
                  default:
                    return false;
                }
              })();

              const rcScore = +resident.recoveryCapitalTotal || 0;
              const residentName =
                resident.residentName ||
                resident.fullName ||
                `${resident.firstName} ${resident.lastName}`.trim();
              const relapsed = isRelapse(
                allData.alumniMap.get(residentName)?.sobrietyStatus // ✅ Use residentName here too
              );

              let targetGroup;
              if (hasFactorA && hasFactorB) targetGroup = groups.bothFactors;
              else if (hasFactorA && !hasFactorB)
                targetGroup = groups.factorAOnly;
              else if (!hasFactorA && hasFactorB)
                targetGroup = groups.factorBOnly;
              else targetGroup = groups.neitherFactor;

              targetGroup.residents.push(resident);
              targetGroup.rcScores.push(rcScore);
              if (relapsed) targetGroup.relapses++;
            });

            // Calculate averages
            const calculateGroupStats = (group) => ({
              count: group.residents.length,
              avgRC:
                group.rcScores.length > 0
                  ? +(
                      group.rcScores.reduce((sum, score) => sum + score, 0) /
                      group.rcScores.length
                    ).toFixed(1)
                  : 0,
              relapseRate:
                group.residents.length > 0
                  ? +((group.relapses / group.residents.length) * 100).toFixed(
                      1
                    )
                  : 0,
              rcDiff:
                group.rcScores.length > 0
                  ? +(
                      group.rcScores.reduce((sum, score) => sum + score, 0) /
                        group.rcScores.length -
                      (groups.neitherFactor.rcScores.length > 0
                        ? groups.neitherFactor.rcScores.reduce(
                            (sum, score) => sum + score,
                            0
                          ) / groups.neitherFactor.rcScores.length
                        : 0)
                    ).toFixed(1)
                  : 0,
            });

            const bothStats = calculateGroupStats(groups.bothFactors);
            const factorAStats = calculateGroupStats(groups.factorAOnly);
            const factorBStats = calculateGroupStats(groups.factorBOnly);
            const neitherStats = calculateGroupStats(groups.neitherFactor);

            return (
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
                <h3 className="text-xl font-bold text-blue-400 mb-4">
                  2-Factor Interaction Analysis: {factorALabel} × {factorBLabel}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-900/50 p-4 rounded-lg border-2 border-emerald-500/30">
                    <p className="text-emerald-400 font-medium mb-2">
                      Both Factors
                    </p>
                    <p className="text-white text-2xl font-bold">
                      RC {bothStats.rcDiff > 0 ? "+" : ""}
                      {bothStats.rcDiff}
                      <span className="text-sm text-slate-400 ml-2">
                        ({bothStats.avgRC} avg)
                      </span>
                    </p>
                    <p className="text-red-400 text-sm">
                      {bothStats.relapseRate}% relapse
                    </p>
                    <p className="text-slate-500 text-xs">
                      n={bothStats.count}
                    </p>
                  </div>

                  <div className="bg-slate-900/50 p-4 rounded-lg">
                    <p className="text-blue-400 font-medium mb-2">
                      {factorALabel} Only
                    </p>
                    <p className="text-white text-2xl font-bold">
                      RC {factorAStats.rcDiff > 0 ? "+" : ""}
                      {factorAStats.rcDiff}
                      <span className="text-sm text-slate-400 ml-2">
                        ({factorAStats.avgRC} avg)
                      </span>
                    </p>
                    <p className="text-red-400 text-sm">
                      {factorAStats.relapseRate}% relapse
                    </p>
                    <p className="text-slate-500 text-xs">
                      n={factorAStats.count}
                    </p>
                  </div>

                  <div className="bg-slate-900/50 p-4 rounded-lg">
                    <p className="text-purple-400 font-medium mb-2">
                      {factorBLabel} Only
                    </p>
                    <p className="text-white text-2xl font-bold">
                      RC {factorBStats.rcDiff > 0 ? "+" : ""}
                      {factorBStats.rcDiff}
                      <span className="text-sm text-slate-400 ml-2">
                        ({factorBStats.avgRC} avg)
                      </span>
                    </p>
                    <p className="text-red-400 text-sm">
                      {factorBStats.relapseRate}% relapse
                    </p>
                    <p className="text-slate-500 text-xs">
                      n={factorBStats.count}
                    </p>
                  </div>

                  <div className="bg-slate-900/50 p-4 rounded-lg border-2 border-red-500/30">
                    <p className="text-red-400 font-medium mb-2">
                      Neither Factor
                    </p>
                    <p className="text-white text-2xl font-bold">
                      RC {neitherStats.rcDiff > 0 ? "+" : ""}
                      {neitherStats.rcDiff}
                      <span className="text-sm text-slate-400 ml-2">
                        ({neitherStats.avgRC} avg)
                      </span>
                    </p>
                    <p className="text-red-400 text-sm">
                      {neitherStats.relapseRate}% relapse
                    </p>
                    <p className="text-slate-500 text-xs">
                      n={neitherStats.count}
                    </p>
                  </div>
                </div>

                {/* Synergy Analysis */}
                <div className="mt-4 p-3 bg-slate-900/50 rounded-lg">
                  <h5 className="text-white font-medium mb-2">
                    Synergy Analysis
                  </h5>
                  {(() => {
                    const expectedCombined =
                      factorAStats.rcDiff + factorBStats.rcDiff;
                    const actualCombined = bothStats.rcDiff;
                    const synergy = actualCombined - expectedCombined;

                    return (
                      <p className="text-sm text-slate-300">
                        <span
                          className={
                            synergy > 5
                              ? "text-emerald-400"
                              : synergy < -5
                              ? "text-red-400"
                              : "text-slate-300"
                          }
                        >
                          {synergy > 5
                            ? "Strong Positive Synergy"
                            : synergy < -5
                            ? "Negative Interaction"
                            : "Additive Effect"}
                        </span>
                        : Combined effect ({actualCombined.toFixed(1)}) vs
                        expected additive ({expectedCombined.toFixed(1)}) ={" "}
                        {synergy > 0 ? "+" : ""}
                        {synergy.toFixed(1)} synergy points
                      </p>
                    );
                  })()}
                </div>
              </div>
            );
          }
          return null;
        })()}

      {/* Show filtered results or all data */}
      {hasFilteredData ? (
        <>
          {/* Filtered Factor Rankings */}
          {filteredData.factorRankings.length > 0 && (
            <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
              <h3 className="text-xl font-bold text-blue-400 mb-4">
                Factor Impact for Selected Criteria
              </h3>
              <div className="space-y-3">
                {filteredData.factorRankings.map((impact, index) => (
                  <div key={index}>
                    <div className="flex justify-between mb-1">
                      <span className="text-slate-300 text-sm">
                        {impact.factor}
                      </span>
                      <span
                        className={`font-bold ${
                          impact.rcDifference > 0
                            ? "text-emerald-400"
                            : "text-red-400"
                        }`}
                      >
                        {impact.rcDifference > 0 ? "+" : ""}
                        {Math.abs(impact.rcDifference).toFixed(1)} RC pts
                      </span>
                    </div>
                    <div className="relative h-2 w-full bg-slate-700/30 rounded-full">
                      {impact.rcDifference > 0 ? (
                        <div
                          className={`absolute left-1/2 h-2 ${impact.color} rounded-r-full`}
                          style={{
                            width: `${Math.min(
                              Math.abs(impact.rcDifference) * 2,
                              50
                            )}%`,
                            maxWidth: "50%",
                          }}
                        ></div>
                      ) : (
                        <div
                          className={`absolute right-1/2 h-2 ${impact.color} rounded-l-full`}
                          style={{
                            width: `${Math.min(
                              Math.abs(impact.rcDifference) * 2,
                              50
                            )}%`,
                            maxWidth: "50%",
                          }}
                        ></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filtered Domain Impacts */}
          {Object.keys(filteredData.domainImpacts).length > 0 && (
            <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
              <h3 className="text-xl font-bold text-blue-400 mb-4">
                Domain-Specific Impact for Selected Factors
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
                {Object.values(filteredData.domainImpacts).map(
                  (impact, index) => (
                    <DomainImpactCard key={index} impact={impact} />
                  )
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        // Show all data when no filters are active
        allData && (
          <>
            {/* Original Factor Rankings */}
            <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50 mb-6">
              <h3 className="text-xl font-bold text-blue-400 mb-4">
                Ranked Factor Impact on Recovery Capital
              </h3>
              <div className="space-y-3">
                {allData.factorRankings.map((impact, index) => (
                  <div key={index}>
                    <div className="flex justify-between mb-1">
                      <span className="text-slate-300 text-sm">
                        {impact.factor}
                      </span>
                      <span
                        className={`font-bold ${
                          impact.rcDifference > 0
                            ? "text-emerald-400"
                            : "text-red-400"
                        }`}
                      >
                        {impact.rcDifference > 0 ? "+" : ""}
                        {Math.abs(impact.rcDifference).toFixed(1)} RC pts
                      </span>
                    </div>
                    <div className="relative h-2 w-full bg-slate-700/30 rounded-full">
                      {impact.rcDifference > 0 ? (
                        <div
                          className={`absolute left-1/2 h-2 ${impact.color} rounded-r-full`}
                          style={{
                            width: `${Math.min(
                              Math.abs(impact.rcDifference) * 2,
                              50
                            )}%`,
                            maxWidth: "50%",
                          }}
                        ></div>
                      ) : (
                        <div
                          className={`absolute right-1/2 h-2 ${impact.color} rounded-l-full`}
                          style={{
                            width: `${Math.min(
                              Math.abs(impact.rcDifference) * 2,
                              50
                            )}%`,
                            maxWidth: "50%",
                          }}
                        ></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-slate-900/50 rounded-lg text-sm text-slate-300">
                <p>
                  This visualization ranks factors by their impact on Recovery
                  Capital scores. Factors to the right represent protective
                  elements associated with higher RC, while factors to the left
                  represent risk elements associated with lower RC. Use filters
                  above to focus on specific populations.
                </p>
              </div>
            </div>

            {/* Original Domain Impacts */}
            <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
              <h3 className="text-xl font-bold text-blue-400 mb-4">
                Domain-Specific Factor Impact
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
                {Object.values(allData.domainImpacts).map((impact, index) => (
                  <DomainImpactCard key={index} impact={impact} />
                ))}
              </div>
              <div className="mt-4 bg-slate-900/50 p-3 rounded-lg text-sm text-slate-300">
                <p>
                  This visualization shows how different factors impact specific
                  domains of recovery capital. Longer bars indicate stronger
                  influences (either positive or negative) on that domain.
                </p>
              </div>
            </div>

            {/* Traditional Charts Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Trauma Impact Chart */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl overflow-hidden border border-slate-700/50">
                <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-500/10 rounded-lg">
                      <AlertTriangle className="text-red-400" size={20} />
                    </div>
                    <h3 className="text-lg font-semibold text-white">
                      Trauma Impact
                    </h3>
                  </div>
                </div>
                <div className="p-6">
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={calculateTraumaImpact(
                          allData.rawBuckets.traumaRelapse
                        )}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="rgba(255,255,255,0.1)"
                        />
                        <XAxis dataKey="name" stroke="#94a3b8" />
                        <YAxis
                          yAxisId="left"
                          orientation="left"
                          stroke="#10b981"
                          domain={[0, 200]}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          stroke="#f87171"
                          domain={[0, 35]}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#1e293b",
                            border: "1px solid #334155",
                            borderRadius: "0.5rem",
                          }}
                          itemStyle={{ color: "#e2e8f0" }}
                          labelStyle={{ color: "#f8fafc", fontWeight: "bold" }}
                          formatter={(value, name, props) => {
                            if (name === "RC Score") {
                              return [
                                `${value} (n=${props.payload.count})`,
                                name,
                              ];
                            }
                            return [value, name];
                          }}
                        />
                        <Legend />
                        <Bar
                          yAxisId="left"
                          dataKey="rcScore"
                          name="RC Score"
                          fill="#10b981"
                          radius={[4, 4, 0, 0]}
                        />
                        <Bar
                          yAxisId="right"
                          dataKey="relapse"
                          name="Relapse %"
                          fill="#f87171"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-4 p-4 bg-slate-900/50 rounded-lg">
                    <p className="text-slate-300 text-sm">
                      <span className="text-red-400 font-medium">
                        Trauma Impact:
                      </span>{" "}
                      Shows relationship between childhood trauma and both RC
                      scores and relapse rates. Use the trauma filter above to
                      focus on this population.
                    </p>
                  </div>
                </div>
              </div>

              {/* Age Groups Chart */}
              <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl overflow-hidden border border-slate-700/50">
                <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-500/10 rounded-lg">
                      <Users className="text-emerald-400" size={20} />
                    </div>
                    <h3 className="text-lg font-semibold text-white">
                      Age Group Analysis
                    </h3>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-6">
                    <div className="bg-slate-900/50 p-4 rounded-lg">
                      <h4 className="text-emerald-400 font-medium mb-2">
                        Age Groups
                      </h4>
                      <div className="grid grid-cols-5 gap-2 text-center">
                        {allData.ageGroupStats.map((group, index) => (
                          <div
                            key={index}
                            className="bg-slate-800/50 p-2 rounded"
                          >
                            <p className="text-white text-xs">
                              {group.ageGroup}
                            </p>
                            <p className="text-emerald-400 font-bold">
                              {group.rcScore}
                            </p>
                            <p className="text-red-400 text-xs">
                              {group.relapseRate}%
                            </p>
                            <p className="text-slate-500 text-xs">
                              n={group.count}
                            </p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 text-xs text-center text-slate-400">
                        <span className="text-emerald-400">RC Score</span> |{" "}
                        <span className="text-red-400">Relapse %</span> |{" "}
                        <span className="text-slate-500">Sample Size</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Usage Instructions */}
            <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
              <h3 className="text-xl font-bold text-emerald-400 mb-4 flex items-center gap-2">
                <Filter size={20} />
                How to Use Filters
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="text-white font-semibold">
                    Filter Combinations
                  </h4>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 font-bold">•</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 font-bold">•</span>
                      Mix risk and protective factors to analyze complex
                      profiles
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 font-bold">•</span>
                      Count badges show how many residents match each individual
                      factor
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 font-bold">•</span>
                      Filter summary shows total matching residents and their
                      outcomes
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 font-bold">★</span>
                      Select exactly 2 factors to see detailed interaction
                      analysis
                    </li>
                  </ul>
                </div>
                <div className="space-y-4">
                  <h4 className="text-white font-semibold">
                    Example Use Cases
                  </h4>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 font-bold">✓</span>
                      Find residents with trauma + therapy to see intervention
                      effectiveness
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 font-bold">✓</span>
                      Compare high-risk profiles (multiple risk factors
                      selected)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 font-bold">✓</span>
                      Identify resilient residents (risk + protective factors)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400 font-bold">✓</span>
                      Target specific interventions based on factor combinations
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 font-bold">⚡</span>
                      Use 2-factor analysis to discover synergistic effects
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        )
      )}

      {/* SUCCESS BLUEPRINT SECTION */}
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-8 border border-slate-700/50">
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-emerald-500/20 rounded-xl">
            <Award className="text-emerald-400" size={32} />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-white mb-2">
              🏆 Success Blueprint
            </h3>
            <p className="text-emerald-300 text-lg">
              Data-driven insights for optimal recovery outcomes
            </p>
          </div>
        </div>

        {/* Analysis Controls */}
        <div className="bg-slate-900/50 rounded-xl p-6 mb-8 border border-slate-700/50">
          <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Filter size={16} />
            </div>
            Analysis Controls
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Minimum Sample Size
              </label>
              <input
                type="number"
                min="1"
                max="50"
                value={analysisSettings.minSampleSize}
                onChange={(e) =>
                  setAnalysisSettings((prev) => ({
                    ...prev,
                    minSampleSize: parseInt(e.target.value) || 5,
                  }))
                }
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Maximum Relapse Rate %
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={analysisSettings.maxRelapseRate}
                onChange={(e) =>
                  setAnalysisSettings((prev) => ({
                    ...prev,
                    maxRelapseRate: parseInt(e.target.value) || 70,
                  }))
                }
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Minimum RC Score
              </label>
              <input
                type="number"
                min="0"
                max="200"
                value={analysisSettings.minRCScore}
                onChange={(e) =>
                  setAnalysisSettings((prev) => ({
                    ...prev,
                    minRCScore: parseInt(e.target.value) || 0,
                  }))
                }
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Sort Priority
              </label>
              <select
                value={analysisSettings.sortBy}
                onChange={(e) =>
                  setAnalysisSettings((prev) => ({
                    ...prev,
                    sortBy: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              >
                <option value="relapseRate">Lowest Relapse Rate</option>
                <option value="rcScore">Highest RC Score</option>
                <option value="sampleSize">Largest Sample</option>
                <option value="balanced">Balanced Score</option>
              </select>
            </div>
          </div>

          <button
            onClick={() =>
              setAnalysisSettings({
                minSampleSize: 3,
                maxRelapseRate: 50,
                minRCScore: 0,
                sortBy: "relapseRate",
              })
            }
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm transition-colors flex items-center gap-2"
          >
            <RotateCcw size={14} />
            Reset to Defaults
          </button>
        </div>

        {(() => {
          if (!allData?.rowsForStats) return null;

          // Calculate all combinations for success analysis
          const allFactors = {
            trauma: (r) => checkResponse(r.childhoodTrauma) === "yes",
            cod: (r) => checkResponse(r.coOccurringDiagnosis) === "yes",
            prison: (r) => checkResponse(r.overOneYearInPrison) === "yes",
            homeless: (r) => checkResponse(r.homelessOneYear) === "yes",
            guilt: (r) => hasGuiltShame(r.guiltOrShame),
            purpose: (r) => {
              const purposeItems = ["lifeHasPurpose", "greaterPurpose"]
                .map((f) => +r[f])
                .filter((v) => !Number.isNaN(v));
              return (
                purposeItems.length &&
                purposeItems.reduce((s, v) => s + v, 0) / purposeItems.length >=
                  3
              );
            },
            twoParentHome: (r) => checkResponse(r.twoParentsHome) === "yes",
            therapy: (r) => isInTherapy(r.therapy),
            strongKidsRelation: (r) =>
              checkResponse(r.kids) === "yes" &&
              hasStrongKidsRelation(r.strongRelationKids),
            kids: (r) => checkResponse(r.kids) === "yes",
            married: (r) => checkResponse(r.married) === "yes",
          };

          const factorLabels = {
            trauma: "Childhood Trauma",
            cod: "Co-occurring Diagnosis",
            prison: "Prison Experience",
            homeless: "Homelessness",
            guilt: "Guilt/Shame",
            purpose: "Sense of Purpose",
            twoParentHome: "Two-Parent Home",
            therapy: "In Therapy",
            strongKidsRelation: "Strong Kids Relationship",
            kids: "Has Children",
            married: "Married",
          };

          const calculateStats = (residents) => {
            if (residents.length === 0)
              return { count: 0, avgRC: 0, relapseRate: 0, alumniCount: 0 };

            const rcScores = residents.map((r) => +r.recoveryCapitalTotal || 0);
            const avgRC =
              rcScores.reduce((sum, score) => sum + score, 0) / rcScores.length;

            const alumniWithOutcomes = residents.filter((r) => {
              const name =
                r.residentName ||
                r.fullName ||
                `${r.firstName} ${r.lastName}`.trim();
              const alumni = allData.alumniMap.get(name);
              return alumni && alumni.sobrietyStatus;
            });

            const relapses = alumniWithOutcomes.filter((r) => {
              const name =
                r.residentName ||
                r.fullName ||
                `${r.firstName} ${r.lastName}`.trim();
              return isRelapse(allData.alumniMap.get(name)?.sobrietyStatus);
            }).length;

            const relapseRate =
              alumniWithOutcomes.length > 0
                ? (relapses / alumniWithOutcomes.length) * 100
                : 0;

            return {
              count: residents.length,
              avgRC: +avgRC.toFixed(1),
              relapseRate: +relapseRate.toFixed(1),
              alumniCount: alumniWithOutcomes.length,
            };
          };

          // Generate combinations
          const getCombinations = (arr, size) => {
            if (size === 1) return arr.map((item) => [item]);
            const combinations = [];
            for (let i = 0; i < arr.length; i++) {
              const smaller = getCombinations(arr.slice(i + 1), size - 1);
              smaller.forEach((combo) => combinations.push([arr[i], ...combo]));
            }
            return combinations;
          };

          const factorKeys = Object.keys(allFactors);
          const allCombinations = [];

          // Generate combinations of 2-4 factors
          for (let size = 2; size <= 4; size++) {
            const combinations = getCombinations(factorKeys, size);
            combinations.forEach((combo) => {
              const matchingResidents = allData.rowsForStats.filter(
                (resident) => {
                  return combo.every((factor) => allFactors[factor](resident));
                }
              );

              const stats = calculateStats(matchingResidents);

              if (stats.alumniCount >= analysisSettings.minSampleSize) {
                allCombinations.push({
                  factors: combo.map((f) => factorLabels[f]),
                  factorKeys: combo,
                  stats: stats,
                  successScore:
                    stats.alumniCount >= 5
                      ? 100 - stats.relapseRate + stats.avgRC / 10
                      : (100 - stats.relapseRate) * 0.7 + stats.avgRC / 10,
                });
              }
            });
          }

          // Apply user filters and sorting
          const filteredCombinations = allCombinations.filter(
            (combo) =>
              combo.stats.relapseRate <= analysisSettings.maxRelapseRate &&
              combo.stats.avgRC >= analysisSettings.minRCScore
          );

          // Sort based on user preference
          const sortedCombinations = filteredCombinations.sort((a, b) => {
            switch (analysisSettings.sortBy) {
              case "relapseRate":
                if (a.stats.relapseRate !== b.stats.relapseRate) {
                  return a.stats.relapseRate - b.stats.relapseRate;
                }
                return b.stats.alumniCount - a.stats.alumniCount;
              case "rcScore":
                if (a.stats.avgRC !== b.stats.avgRC) {
                  return b.stats.avgRC - a.stats.avgRC;
                }
                return b.stats.alumniCount - a.stats.alumniCount;
              case "sampleSize":
                if (a.stats.alumniCount !== b.stats.alumniCount) {
                  return b.stats.alumniCount - a.stats.alumniCount;
                }
                return a.stats.relapseRate - b.stats.relapseRate;
              case "balanced":
                const scoreA = 100 - a.stats.relapseRate + a.stats.avgRC / 2;
                const scoreB = 100 - b.stats.relapseRate + b.stats.avgRC / 2;
                return scoreB - scoreA;
              default:
                return a.stats.relapseRate - b.stats.relapseRate;
            }
          });

          // Get top 3 combinations based on filtered and sorted results
          const topCombinations = sortedCombinations.slice(0, 3);

          // Get protective factors analysis
          // Get protective factors analysis
          const protectiveFactors = [
            "purpose",
            "twoParentHome",
            "therapy",
            "strongKidsRelation",
          ];

          const singleFactorImpacts = protectiveFactors
            .map((factor) => {
              const withFactor = allData.rowsForStats.filter(
                allFactors[factor]
              );
              const withoutFactor = allData.rowsForStats.filter(
                (r) => !allFactors[factor](r)
              );

              const withStats = calculateStats(withFactor);
              const withoutStats = calculateStats(withoutFactor);

              return {
                factor: factorLabels[factor],
                impact: withStats.avgRC - withoutStats.avgRC,
                relapseReduction:
                  withoutStats.relapseRate - withStats.relapseRate,
                withStats,
                withoutStats,
                // Add these sample size properties
                withSampleSize: withFactor.length,
                withoutSampleSize: withoutFactor.length,
              };
            })
            .sort((a, b) => b.impact - a.impact);

          return (
            <div className="space-y-8">
              {/* Top Success Combinations */}
              <div>
                <h4 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-black font-bold">
                    1
                  </div>
                  Best Combinations Meeting Your Criteria
                </h4>

                {/* Results Summary */}
                <div className="mb-6 p-4 bg-slate-900/50 rounded-lg border border-slate-700/50">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="text-lg font-bold text-blue-400">
                        {sortedCombinations.length}
                      </div>
                      <div className="text-xs text-slate-400">
                        Total Matches
                      </div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-emerald-400">
                        {sortedCombinations.length > 0
                          ? Math.min(
                              ...sortedCombinations.map(
                                (c) => c.stats.relapseRate
                              )
                            )
                          : 0}
                        %
                      </div>
                      <div className="text-xs text-slate-400">
                        Best Relapse Rate
                      </div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-purple-400">
                        {sortedCombinations.length > 0
                          ? Math.max(
                              ...sortedCombinations.map((c) => c.stats.avgRC)
                            ).toFixed(1)
                          : 0}
                      </div>
                      <div className="text-xs text-slate-400">
                        Highest RC Score
                      </div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-amber-400">
                        {sortedCombinations.length > 0
                          ? Math.max(
                              ...sortedCombinations.map(
                                (c) => c.stats.alumniCount
                              )
                            )
                          : 0}
                      </div>
                      <div className="text-xs text-slate-400">
                        Largest Sample
                      </div>
                    </div>
                  </div>

                  {sortedCombinations.length === 0 && (
                    <div className="text-center py-4 text-amber-400">
                      <Filter className="mx-auto mb-2" size={24} />
                      <p className="font-medium">
                        No combinations meet your current criteria
                      </p>
                      <p className="text-sm text-slate-400 mt-1">
                        Try adjusting the filters above to see more results
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {topCombinations.length > 0 ? (
                    topCombinations.map((combo, index) => (
                      <div
                        key={index}
                        className="bg-slate-800/60 backdrop-blur-sm rounded-xl p-6 border border-emerald-500/20 hover:border-emerald-400/40 transition-all duration-300 group"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-r from-emerald-400 to-blue-400 rounded-full flex items-center justify-center text-black font-bold text-sm">
                              #{index + 1}
                            </div>
                            <span className="text-emerald-400 font-semibold">
                              {combo.stats.relapseRate === 0
                                ? "PERFECT"
                                : "TOP"}{" "}
                              PROFILE
                            </span>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-white">
                              {combo.stats.relapseRate}%
                            </div>
                            <div className="text-xs text-emerald-300">
                              Relapse Rate
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2 mb-4">
                          {combo.factors.map((factor, i) => (
                            <div
                              key={i}
                              className="flex items-center gap-2 text-sm"
                            >
                              <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                              <span className="text-slate-200">{factor}</span>
                            </div>
                          ))}
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-center">
                          <div className="bg-slate-900/50 p-3 rounded-lg">
                            <div className="text-lg font-bold text-blue-400">
                              {combo.stats.avgRC}
                            </div>
                            <div className="text-xs text-slate-400">
                              Avg RC Score
                            </div>
                          </div>
                          <div className="bg-slate-900/50 p-3 rounded-lg">
                            <div className="text-lg font-bold text-purple-400">
                              {combo.stats.alumniCount}
                            </div>
                            <div className="text-xs text-slate-400">
                              Alumni Sample
                            </div>
                          </div>
                        </div>

                        {/* Quality indicators */}
                        <div className="mt-3 flex flex-wrap gap-1">
                          {combo.stats.relapseRate === 0 && (
                            <span className="px-2 py-1 bg-emerald-900/50 text-emerald-300 text-xs rounded-full">
                              Zero Relapse
                            </span>
                          )}
                          {combo.stats.avgRC >= 150 && (
                            <span className="px-2 py-1 bg-blue-900/50 text-blue-300 text-xs rounded-full">
                              High RC
                            </span>
                          )}
                          {combo.stats.alumniCount >= 10 && (
                            <span className="px-2 py-1 bg-purple-900/50 text-purple-300 text-xs rounded-full">
                              Large Sample
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-3 text-center py-8">
                      <div className="text-slate-400 mb-4">
                        <Filter size={48} className="mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium">
                          No combinations found
                        </p>
                        <p className="text-sm">
                          Try adjusting your criteria above
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Key Protective Factors */}
              <div>
                <h4 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                    2
                  </div>
                  Most Protective Single Factors
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {singleFactorImpacts.slice(0, 4).map((factor, index) => (
                    <div
                      key={index}
                      className="bg-slate-800/60 backdrop-blur-sm rounded-xl p-6 border border-blue-500/20 hover:border-blue-400/40 transition-all duration-300"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h5 className="text-lg font-semibold text-white">
                          {factor.factor}
                        </h5>
                        <div className="text-right">
                          <div className="text-xl font-bold text-emerald-400">
                            +{factor.impact.toFixed(1)}
                          </div>
                          <div className="text-xs text-emerald-300">
                            RC Point Boost
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="text-center">
                          <div className="text-sm text-slate-400 mb-1">
                            With Factor
                          </div>
                          <div className="text-emerald-400 font-bold">
                            {factor.withStats.relapseRate}% relapse
                          </div>
                          <div className="text-blue-400 text-sm">
                            RC: {factor.withStats.avgRC}
                          </div>
                          {/* ADD THIS LINE */}
                          <div className="text-slate-500 text-xs">
                            n = {factor.withSampleSize} (
                            {factor.withStats.alumniCount} alumni)
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm text-slate-400 mb-1">
                            Without Factor
                          </div>
                          <div className="text-red-400 font-bold">
                            {factor.withoutStats.relapseRate}% relapse
                          </div>
                          <div className="text-slate-400 text-sm">
                            RC: {factor.withoutStats.avgRC}
                          </div>
                          {/* ADD THIS LINE */}
                          <div className="text-slate-500 text-xs">
                            n = {factor.withoutSampleSize} (
                            {factor.withoutStats.alumniCount} alumni)
                          </div>
                        </div>
                      </div>

                      {factor.relapseReduction !== 0 && (
                        <div
                          className={`p-3 rounded-lg text-center ${
                            factor.relapseReduction > 0
                              ? "bg-emerald-900/30"
                              : "bg-red-900/30"
                          }`}
                        >
                          <div
                            className={`font-semibold ${
                              factor.relapseReduction > 0
                                ? "text-emerald-400"
                                : "text-red-400"
                            }`}
                          >
                            {factor.relapseReduction > 0 ? "-" : "+"}
                            {Math.abs(factor.relapseReduction).toFixed(1)}%
                            relapse
                            {factor.relapseReduction > 0
                              ? " reduction"
                              : " increase"}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Usage Instructions */}
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
        <h3 className="text-xl font-bold text-emerald-400 mb-4 flex items-center gap-2">
          <Filter size={20} />
          How to Use Filters
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="text-white font-semibold">Filter Combinations</h4>
            <ul className="space-y-2 text-sm text-slate-300">
              <li className="flex items-start gap-2">
                <span className="text-blue-400 font-bold">•</span>
                Select individual factors to see their isolated impact
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 font-bold">•</span>
                Mix risk and protective factors to analyze complex profiles
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 font-bold">•</span>
                Count badges show how many residents match each individual
                factor
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 font-bold">•</span>
                Filter summary shows total matching residents and their outcomes
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 font-bold">★</span>
                Select exactly 2 factors to see detailed interaction analysis
              </li>
            </ul>
          </div>
          <div className="space-y-4">
            <h4 className="text-white font-semibold">Example Use Cases</h4>
            <ul className="space-y-2 text-sm text-slate-300">
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 font-bold">✓</span>
                Find residents with trauma + therapy to see intervention
                effectiveness
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 font-bold">✓</span>
                Compare high-risk profiles (multiple risk factors selected)
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 font-bold">✓</span>
                Identify resilient residents (risk + protective factors)
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 font-bold">✓</span>
                Target specific interventions based on factor combinations
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 font-bold">⚡</span>
                Use 2-factor analysis to discover synergistic effects
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RCKeyFactorsTab;
